/**
 * Container API
 *
 * API functions for container lifecycle management.
 * All functions use authFetch for authenticated requests.
 *
 * @module features/tenants/api/containerApi
 */

import type { ContainerStatus, ContainerProvisioningProgress } from '../types';

const API_BASE = '/api/admin/containers';

// Type for the authFetch function signature
type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

// ============================================================================
// Container Logs
// ============================================================================

export interface ContainerLogsResponse {
  tenant_id: string;
  tenant_slug: string;
  service: string;
  lines: number;
  logs: string;
}

/**
 * Fetch container logs for a tenant service.
 */
export const fetchContainerLogs = async (
  authFetch: AuthFetch,
  tenantId: string,
  service: 'api' | 'postgres' | 'redis' = 'api',
  tail: number = 100
): Promise<ContainerLogsResponse> => {
  const params = new URLSearchParams({ service, tail: tail.toString() });
  const response = await authFetch(`${API_BASE}/${tenantId}/logs?${params}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to fetch logs');
  }
  return response.json();
};

// ============================================================================
// Container Status
// ============================================================================

/**
 * Fetch container status for a tenant.
 */
export const fetchContainerStatus = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<ContainerStatus | null> => {
  try {
    const response = await authFetch(`${API_BASE}/${tenantId}/status`);
    if (response.ok) {
      return response.json();
    }
    return null;
  } catch {
    return null;
  }
};

// ============================================================================
// Container Provisioning
// ============================================================================

/**
 * Start container provisioning for a tenant.
 */
export const provisionContainers = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<void> => {
  const response = await authFetch(`${API_BASE}/${tenantId}/provision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}), // Uses tier defaults
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to start container provisioning: ${response.statusText}`);
  }
};

/**
 * Fetch container provisioning status.
 */
export const fetchContainerProvisioningStatus = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<ContainerProvisioningProgress> => {
  const response = await authFetch(`${API_BASE}/${tenantId}/provision/status`);
  if (!response.ok) {
    throw new Error(`Failed to fetch provisioning status: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Reset stuck provisioning status.
 * Use when a tenant is stuck in 'provisioning' state due to interruption.
 */
export const resetProvisioningStatus = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<ContainerProvisioningProgress> => {
  const response = await authFetch(`${API_BASE}/${tenantId}/provision/reset`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to reset provisioning status: ${response.statusText}`);
  }
  return response.json();
};

// ============================================================================
// Container Lifecycle
// ============================================================================

/**
 * Container action type.
 */
export type ContainerAction = 'start' | 'stop' | 'restart';

/**
 * Perform a container action (start, stop, restart).
 */
export const containerAction = async (
  authFetch: AuthFetch,
  tenantId: string,
  action: ContainerAction
): Promise<ContainerStatus> => {
  const response = await authFetch(`${API_BASE}/${tenantId}/${action}`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to ${action} containers: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Start containers for a tenant.
 */
export const startContainers = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<ContainerStatus> => {
  return containerAction(authFetch, tenantId, 'start');
};

/**
 * Stop containers for a tenant.
 */
export const stopContainers = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<ContainerStatus> => {
  return containerAction(authFetch, tenantId, 'stop');
};

/**
 * Restart containers for a tenant.
 */
export const restartContainers = async (
  authFetch: AuthFetch,
  tenantId: string
): Promise<ContainerStatus> => {
  return containerAction(authFetch, tenantId, 'restart');
};

/**
 * Destroy containers for a tenant.
 */
export const destroyContainers = async (
  authFetch: AuthFetch,
  tenantId: string,
  confirmSlug: string,
  removeVolumes: boolean = true
): Promise<void> => {
  const response = await authFetch(
    `${API_BASE}/${tenantId}?remove_volumes=${removeVolumes}&confirm_slug=${confirmSlug}`,
    { method: 'DELETE' }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to destroy containers: ${response.statusText}`);
  }
};
